import { FC, FocusEvent, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FormFieldsContainer from "../../components/ui/FormFieldsContainer";
import Header from "../../components/ui/Header";
import useImageUploader from "../../components/ui/ImageUploader";
import {
  Button,
  Card,
  FlexContent,
  Form,
  FormField,
  Input,
  Switch,
  Textarea,
  TranslatableField,
} from "@rothoblaas/design-system";
import { translatableFieldProps } from "../../utils/translatableField";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { isNotFound } from "../../http/errors";
import {
  AdminItem,
  AdminItemSummary as ApiAdminItemSummary,
  InsertAutomaticSyncItemsBody,
  InsertManualItemsBody,
  UpdateAutomaticSyncItemsNewImageBody,
  UpdateAutomaticSyncItemsRemoveImageBody,
  UpdateManualItemsNewImageBody,
  UpdateManualItemsRemoveImageBody,
} from "../../http/types/items";
import { useItemsApi } from "../../http/useItems";
import { formatTimeStamp } from "../../utils";

export type AdminItemSummary = ApiAdminItemSummary;

export const NewAdminItem: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header
        title={i18n.translation.items.createNew}
        path={[
          { display: i18n.translation.items.all, url: "/admin/items" },
          { display: i18n.translation.items.createNew },
        ]}
      />

      <NewEditAdminItem />
    </>
  );
};

export const EditAdminItem: FC = () => {
  const i18n = useI18n();
  const { itemId } = useParams<{ itemId: string }>();
  const itemsApi = useItemsApi();

  const aborter = useRef(new AbortController());

  const { error, data, isMutating, trigger } = itemsApi.getAdminItem(itemId!, aborter.current.signal);

  useEffect(() => {
    itemId && trigger();
  }, [itemId]);

  return (
    <>
      <Header
        title={data?.code ?? i18n.translation.common.loading}
        error={error}
        path={[{ display: i18n.translation.items.all, url: "/admin/items" }, { display: data?.code! }]}
      />

      <NewEditAdminItem item={!error ? data : undefined} disabled={isMutating || !!error} onUpdate={trigger} isEdit />
    </>
  );
};

interface NewEditAdminItemProps {
  item?: AdminItem;
  disabled?: boolean;
  isEdit?: boolean;
  onUpdate?: () => void;
  disableNavigationOnSuccess?: boolean;
  onCreateSuccess?: (item: AdminItemSummary) => void;
  onClose?: () => void;
}

interface ItemFormBody {
  code: string | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
  automaticSync: boolean;
}

export const NewEditAdminItem: FC<NewEditAdminItemProps> = ({
  item,
  isEdit,
  disabled,
  onUpdate,
  disableNavigationOnSuccess = false,
  onCreateSuccess,
  onClose,
}: NewEditAdminItemProps) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const api = useApi();
  const itemsApi = useItemsApi();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();

  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: item?.image,
    isLoading: isEdit ? !item : false,
    disabled: disabled ?? false,
  });

  const defaultValues: ItemFormBody = {
    code: item?.code ?? null,
    description: item?.description ?? null,
    descriptionIt: item?.descriptionIt ?? null,
    descriptionDe: item?.descriptionDe ?? null,
    descriptionEs: item?.descriptionEs ?? null,
    descriptionFr: item?.descriptionFr ?? null,
    automaticSync: item?.automaticSync ?? false,
  };

  const onSubmit = () => async (formData: ItemFormBody) => {
    const commonBody = {
      code: formData.code?.trim()!,
      description: formData.description!,
      descriptionIt: formData.descriptionIt?.trim() ?? null,
      descriptionDe: formData.descriptionDe?.trim() ?? null,
      descriptionEs: formData.descriptionEs?.trim() ?? null,
      descriptionFr: formData.descriptionFr?.trim() ?? null,
    };

    setIsSubmitting(true);

    if (item?.id) {
      const submitUpdate = async (
        body:
          | UpdateManualItemsRemoveImageBody
          | UpdateManualItemsNewImageBody
          | UpdateAutomaticSyncItemsRemoveImageBody
          | UpdateAutomaticSyncItemsNewImageBody,
      ) => {
        try {
          await itemsApi.updateAdminItem(item.id, body);
          setIsDirty(false);
          onSave();
          dispatch(showSuccessNotification(i18n.translation));
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
        } finally {
          setIsSubmitting(false);
        }
      };

      if (newImage) {
        try {
          const uploadPolicy = await itemsApi.generateUploadImageUrl(item.id, newImage.name, newImage.type);
          const res = await api.uploadFile(uploadPolicy, newImage);
          if (!res.ok) {
            dispatch(showErrorNotification(i18n, res));
            return;
          }

          const body: UpdateAutomaticSyncItemsNewImageBody | UpdateManualItemsNewImageBody = formData.automaticSync
            ? {
                code: formData.code!,
                image: newImage.name,
                automaticSync: true,
              }
            : {
                ...commonBody,
                image: newImage.name,
                automaticSync: false,
              };

          await submitUpdate(body);
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
        } finally {
          setIsSubmitting(false);
        }
      } else {
        const body: UpdateAutomaticSyncItemsRemoveImageBody | UpdateManualItemsRemoveImageBody = formData.automaticSync
          ? {
              code: formData.code?.trim()!,
              automaticSync: true,
              removeImage,
            }
          : {
              ...commonBody,
              automaticSync: false,
              removeImage,
            };

        await submitUpdate(body);
      }

      return;
    }

    const submitCreate = async (body: InsertManualItemsBody | InsertAutomaticSyncItemsBody) => {
      try {
        const createdItem = await itemsApi.insertAdminItem(body);

        onCreateSuccess?.(createdItem);

        if (!disableNavigationOnSuccess) {
          navigate(`/admin/items/${createdItem.id}`, { replace: true });
        }
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    };

    if (formData.automaticSync) {
      await submitCreate({
        code: formData.code?.trim()!,
        automaticSync: true,
      });
    } else {
      await submitCreate({
        ...commonBody,
        automaticSync: false,
      });
    }
  };

  return (
    <FlexContent padding noPaddingTop direction="row">
      <Card shadow>
        <Form<ItemFormBody>
          submitText={i18n.translation.common.save}
          disabled={disabled || isSubmitting || isSyncing}
          defaultValues={defaultValues}
          secondaryButton={
            <>
              <Button
                disabled={isDirty || disabled || isSubmitting}
                isLoading={isSyncing}
                onClick={() => {
                  if (item?.id) {
                    setIsSyncing(true);
                    itemsApi
                      .syncAdminItem(item.id)
                      .then(() => {
                        onUpdate?.();
                      })
                      .catch((err) => {
                        const msg = isNotFound(err) ? i18n.translation.items.errors.doesNotExist : undefined;
                        dispatch(showErrorNotification(i18n, err, msg));
                      })
                      .finally(() => setIsSyncing(false));
                  }
                }}
                variant="secondary"
              >
                {i18n.translation.common.sync}
              </Button>
              {onClose && (
                <Button onClick={onClose} variant="secondary">
                  {i18n.translation.common.cancel}
                </Button>
              )}
            </>
          }
          onSubmit={onSubmit}
        >
          {({ register, formState: { errors }, setValue, watch, reset }) => {
            useEffect(() => {
              reset(defaultValues);
            }, [item]);

            useEffect(() => {
              register("descriptionIt");
              register("descriptionDe");
              register("descriptionEs");
              register("descriptionFr");
            }, [register]);

            const automaticSync = !!watch("automaticSync");
            const values = watch();
            const isDescriptionDisabled = disabled || automaticSync;

            return (
              <FormFieldsContainer imageUploader={component}>
                <FormField label={i18n.translation.common.code} error={errors.code}>
                  {({ labelId, isOptional, isInvalid }) => {
                    const options = {
                      ...register("code", {
                        required: !isOptional,
                        minLength: 1,
                      }),
                    };
                    return (
                      <Input
                        id={labelId}
                        isInvalid={isInvalid}
                        disabled={disabled}
                        forceUpper
                        {...options}
                        onBlur={(e: FocusEvent<HTMLInputElement>) => {
                          e.target.value !== item?.code && setIsDirty(true);
                          return options.onBlur?.(e);
                        }}
                      />
                    );
                  }}
                </FormField>

                <FormField
                  label={i18n.translation.common.description}
                  error={!automaticSync ? errors.description : undefined}
                >
                  {({ labelId, isOptional, isInvalid }) => {
                    const options = {
                      ...register("description", {
                        required: !isOptional && !automaticSync,
                        minLength: 1,
                      }),
                    };
                    const textarea = (
                      <Textarea
                        id={labelId}
                        isInvalid={isInvalid}
                        disabled={isDescriptionDisabled}
                        {...options}
                        onBlur={(e: FocusEvent<HTMLTextAreaElement>) => {
                          e.target.value !== item?.description && setIsDirty(true);
                          return options.onBlur?.(e);
                        }}
                      />
                    );
                    return isDescriptionDisabled ? (
                      textarea
                    ) : (
                      <TranslatableField
                        {...translatableFieldProps("description", values, setValue, i18n.translation)}
                        dialogTitle={i18n.translation.common.description}
                        type="textarea"
                      >
                        {textarea}
                      </TranslatableField>
                    );
                  }}
                </FormField>

                <FormField label={i18n.translation.common.automaticSync}>
                  {({ labelId }) => (
                    <Switch
                      id={labelId}
                      checked={automaticSync}
                      disabled={disabled}
                      onChange={(checked) => setValue("automaticSync", checked)}
                    />
                  )}
                </FormField>

                <FormField label={i18n.translation.common.lastSync}>
                  <Input
                    disabled
                    value={
                      item?.lastSync
                        ? formatTimeStamp(item.lastSync, auth.identity?.language)
                        : i18n.translation.common.never
                    }
                  />
                </FormField>

                {/* Synced from Snowflake, read-only (as in the configurator). */}
                <FormField label={i18n.translation.common.minimumOrderQuantity}>
                  <Input disabled value={item ? `${item.minOrderQuantity}` : ""} />
                </FormField>
              </FormFieldsContainer>
            );
          }}
        </Form>
      </Card>
    </FlexContent>
  );
};
